import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Package,
    ClipboardList,
    FileText,
    BarChart3,
    History,
    UserCog,
    Settings as SettingsIcon,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_fdfa1102-6a78-496f-adc3-9918563c18d9/artifacts/a4oqwkvk_Propac%20logo.png";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/customers", label: "Customers", icon: Users },
    { to: "/products", label: "Products", icon: Package },
    { to: "/orders", label: "Orders", icon: ClipboardList },
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/audit-trail", label: "Audit Trail", icon: History, adminOnly: true },
    { to: "/users", label: "User Management", icon: UserCog, adminOnly: true },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function titleFromPath(pathname) {
    const found = NAV.find((n) => pathname.startsWith(n.to));
    return found ? found.label : "ODR Sys";
}

const APP_TITLE = "Propac | ODR Sys";

export default function Layout() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const title = titleFromPath(location.pathname);

    useEffect(() => {
        document.title = `${title} · ${APP_TITLE}`;
    }, [title]);

    const items = NAV.filter((i) => !i.adminOnly || user?.role === "admin");

    return (
        <div className="flex min-h-screen bg-[var(--odr-bg)]">
            {/* Sidebar — desktop */}
            <aside
                className="odr-sidebar hidden md:flex w-64 shrink-0 flex-col"
                data-testid="sidebar"
            >
                <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
                    <img
                        src={LOGO_URL}
                        alt="Pro-Pac"
                        className="w-9 h-9 object-contain"
                    />
                    <div>
                        <div className="text-sm font-bold tracking-wide text-white">
                            Propac <span className="text-gray-400 font-normal">|</span> ODR Sys
                        </div>
                        <div className="text-[11px] uppercase tracking-wider text-gray-400">
                            Pro-Pac Inc.
                        </div>
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
                    {items.map((i) => (
                        <NavLink
                            key={i.to}
                            to={i.to}
                            data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) => (isActive ? "active" : "")}
                        >
                            <i.icon className="w-5 h-5 stroke-2" />
                            <span>{i.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-white/10 p-3 text-xs text-gray-400">
                    <div className="font-mono">v0.1.0 · Phase 1</div>
                </div>
            </aside>

            {/* Sidebar — mobile drawer */}
            {open && (
                <div
                    className="fixed inset-0 z-40 md:hidden"
                    onClick={() => setOpen(false)}
                >
                    <div className="absolute inset-0 bg-black/40" />
                    <aside
                        className="odr-sidebar absolute left-0 top-0 h-full w-64 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 px-4 py-4 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <img
                                    src={LOGO_URL}
                                    alt="Pro-Pac"
                                    className="w-9 h-9 object-contain"
                                />
                                <div className="text-sm font-bold tracking-wide text-white">
                                    Propac <span className="text-gray-400 font-normal">|</span> ODR Sys
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-gray-300 hover:text-white p-1"
                                data-testid="close-mobile-nav"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
                            {items.map((i) => (
                                <NavLink
                                    key={i.to}
                                    to={i.to}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) => (isActive ? "active" : "")}
                                >
                                    <i.icon className="w-5 h-5 stroke-2" />
                                    <span>{i.label}</span>
                                </NavLink>
                            ))}
                        </nav>
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex flex-1 flex-col min-w-0">
                <header
                    className="h-16 bg-white border-b border-gray-200 flex items-center px-4 md:px-6 sticky top-0 z-30"
                    data-testid="topbar"
                >
                    <button
                        className="md:hidden mr-3 text-gray-700 p-2"
                        onClick={() => setOpen(true)}
                        data-testid="open-mobile-nav"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <h1
                        className="text-base md:text-lg font-semibold text-gray-900 tracking-tight"
                        data-testid="page-title"
                    >
                        {title}
                    </h1>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                            <div
                                className="text-sm font-medium text-gray-900"
                                data-testid="current-user-name"
                            >
                                {user?.full_name || user?.username}
                            </div>
                            <div className="text-xs text-gray-500 uppercase tracking-wider">
                                {user?.role}
                            </div>
                        </div>
                        <button
                            onClick={logout}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
                            data-testid="logout-button"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </header>
                <main className="flex-1 p-4 md:p-6">
                    <div className="max-w-[1600px] mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
}
